'use client'

import * as React from 'react'

import { DateRange } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
// import useMedia from '@/lib/hooks/useMedia'
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../ui/dialog'
import { DialogTrigger } from '@radix-ui/react-dialog'
import { compareAsc, compareDesc, format } from 'date-fns'
import { useBookingParams } from '@/hooks/useBookingParams'
import { useIsMobile } from '@/hooks/use-mobile'
import { useTranslations } from 'next-intl'

interface DatePickerWithRangeProps extends React.HTMLAttributes<HTMLDivElement> {
  date: DateRange | undefined
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>
  openPhoneSheet?: boolean
  toggleMenu?: (val: boolean) => void
  open: boolean
  setOpen: (open: boolean) => void
  handleBookingTriggers?: (type: 'search' | 'guests' | 'dates') => void
  disableUrlUpdate?: boolean
}

function setDateParams(queryParams: URLSearchParams, date: DateRange | undefined) {
  if (date?.from) {
    queryParams.set('arrive', format(date.from, 'yyyy-MM-dd'))
  } else {
    queryParams.delete('arrive')
  }

  if (date?.to) {
    queryParams.set('depart', format(date.to, 'yyyy-MM-dd'))
  } else {
    queryParams.delete('depart')
  }
}

export function DatePickerWithRange({
  className,
  children,
  date,
  setDate,
  openPhoneSheet,
  toggleMenu,
  open,
  setOpen,
  handleBookingTriggers,
  disableUrlUpdate = false,
}: DatePickerWithRangeProps) {
  const { params: searchParams, updateParams } = useBookingParams()
  const t = useTranslations()

  const isMobile = useIsMobile()

  const handleSearch = (date: DateRange | undefined) => {
    setDate(date)

    setOpen(false)
    if (toggleMenu) {
      toggleMenu(false)
    }

    if (openPhoneSheet || disableUrlUpdate) {
      handleBookingTriggers?.('guests')
      return
    }

    updateParams((queryParams) => setDateParams(queryParams, date))

    handleBookingTriggers?.('guests')
  }

  const handleOpen = (open: boolean) => {
    setOpen(open)

    if (disableUrlUpdate) return

    if (!open && date) {
      const arrive = searchParams.get('arrive')
      const depart = searchParams.get('depart')

      const fromCompare = compareAsc(arrive || '', format(date.from || '', 'yyyy-MM-dd'))
      const toCompare = compareAsc(depart || '', format(date.to || '', 'yyyy-MM-dd'))

      if (fromCompare + toCompare === 0) return

      updateParams((queryParams) => setDateParams(queryParams, date))
    }
  }

  return (
    <div>
      {isMobile ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>{children}</DialogTrigger>
          <DialogContent className='w-full rounded-lg p-0 sm:max-w-lg'>
            <DialogTitle className='w-full pt-4 pl-4'>{t('pick_dates')}</DialogTitle>
            <div className='relative'>
              <div className='max-h-[calc(100vh_-_300px)] overflow-y-auto'>
                <Calendar
                  autoFocus
                  mode='range'
                  defaultMonth={date?.from}
                  disabled={(date) => {
                    return date.getTime() < new Date().setHours(0, 0, 0, 0)
                  }}
                  startMonth={new Date()}
                  selected={date}
                  onSelect={(dates, selectedDay) => {
                    if (date?.from && date.to) {
                      setDate({ from: selectedDay, to: undefined })
                    } else if (
                      dates?.from &&
                      dates?.to &&
                      dates.from.getTime() === dates.to.getTime()
                    ) {
                      // First date selected - only set 'from', not 'to'
                      setDate({ from: dates.from, to: undefined })
                    } else {
                      setDate(dates)
                    }
                  }}
                  numberOfMonths={12}
                  disableNavigation
                  hideNavigation
                  showOutsideDays={false}
                  weekStartsOn={1}
                  className='w-full pb-20'
                />
              </div>

              <DialogFooter className='absolute right-0 bottom-0 left-0'>
                <div className='flex w-full px-4 pb-4'>
                  <Button
                    className='w-full'
                    onClick={() => {
                      handleSearch(date)
                      setOpen(false)
                    }}
                  >
                    {t('done')}
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Popover modal open={open} onOpenChange={handleOpen}>
          <PopoverTrigger asChild>{children}</PopoverTrigger>
          <PopoverContent className='w-full min-w-xl p-0' align='end'>
            <Calendar
              autoFocus
              mode='range'
              defaultMonth={date?.from}
              disabled={(date) => {
                return date.getTime() < new Date().setHours(0, 0, 0, 0)
              }}
              startMonth={new Date()}
              weekStartsOn={1}
              showOutsideDays={false}
              selected={date}
              onSelect={(dates, selectedDay) => {
                if (date?.from && date.to) {
                  setDate({ from: selectedDay, to: undefined })
                } else if (
                  dates?.from &&
                  dates?.to &&
                  dates.from.getTime() === dates.to.getTime()
                ) {
                  // First date selected - only set 'from', not 'to'
                  setDate({ from: dates.from, to: undefined })
                } else {
                  setDate(dates)
                }
              }}
              numberOfMonths={2}
            />
            <div className='flex w-full justify-end pr-4 pb-4'>
              <Button
                onClick={() => {
                  handleSearch(date)
                  setOpen(false)
                }}
              >
                {t('done')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
